import { Show, useForm } from "@refinedev/antd";
import {
  Button,
  Col,
  Flex,
  Form,
  Checkbox,
  Input,
  InputRef,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from "antd";
import type { RcFile } from "antd/es/upload";
import { EnvContext } from "../../../providers/env-provider";

import { errorNotification, successNotification } from "../../../helpers";
import { AppTitle } from "../../../components";
import {
  CustomNotificationType,
  GroupInfoType,
  PushDispatchStats,
  TeamType,
  testFcmUsers,
} from "../Lib";
import { useCustom } from "@refinedev/core";
import axAPI from "api/axios-instance";
import { useWatch } from "antd/es/form/Form";
import { useContext, useEffect, useRef, useState } from "react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { TextAreaRef } from "antd/es/input/TextArea";
import { SmileOutlined } from "@ant-design/icons";
import { SegmentSelector } from "./SegmentSelector";

const { Text } = Typography;

export const PushNotificationContent = () => {
  const [teams, setTeams] = useState<TeamType[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [cursorPositionTitle, setCursorPositionTitle] = useState<number>(0);
  const [emojiMessageOpen, setEmojiMessageOpen] = useState<boolean>(false);
  const [emojiTittleOpen, setEmojiTittleOpen] = useState<boolean>(false);
  const { data, isLoading } = useCustom({
    dataProviderName: "pushNotificationDataProvider",
    url: "team",
    method: "get",
  });

  const { formProps, formLoading, form, onFinish } =
    useForm<CustomNotificationType>({
      resource: "push-notification",
      action: "create",
      dataProviderName: "pushNotificationDataProvider",
      redirect: "list",
      ...successNotification({
        successData: {
          message: "Push-уведомлениe успешно отправлено",
          type: "success",
        },
        callback: () => form.resetFields(),
      }),
      ...errorNotification({}),
    });

  const leagueId = useWatch("leagueId", form);
  const groupId = useWatch("groupId", form);
  const sendLink = useWatch("sendLink", form);
  const testSend = useWatch("testSend", form);
  const debugToken = useWatch("debugToken", form);
  const useSegments = useWatch("useSegments", form) as boolean;

  // Сброс полей лиг и команд при выборе сегментов
  useEffect(() => {
    if (useSegments) {
      form.setFieldsValue({
        leagueId: null,
        groupId: null,
        teamId: null,
      });
    }
  }, [useSegments, form]);

  // Сброс сегментов при выборе лиг
  useEffect(() => {
    if (leagueId !== null && leagueId !== undefined) {
      form.setFieldsValue({
        useSegments: false,
        segmentIds: [],
      });
    }
  }, [leagueId, form]);

  // Сброс groupId при изменении leagueId
  useEffect(() => {
    if (leagueId !== null && leagueId !== undefined) {
      form.setFieldsValue({
        groupId: null,
        teamId: null,
      });
    }
  }, [leagueId, form]);

  // Сброс teamId при изменении groupId
  useEffect(() => {
    if (groupId !== null && groupId !== undefined) {
      form.setFieldsValue({
        teamId: null,
      });
    }
  }, [groupId, form]);

  const link = useWatch("link", form);
  const imageUrl = useWatch("imageUrl", form);
  const { THUMBOR_URL } = useContext(EnvContext);

  // 9324: картинка уходит в пуш абсолютной ссылкой на S3. Лимит — 1 МБ:
  // Android режет большие картинки, а качается она по мобильной сети
  // до показа баннера.
  const uploadPushImage = async (file: RcFile) => {
    if (file.size > 1024 * 1024) {
      notification.error({ message: "Картинка больше 1 МБ" });
      return Upload.LIST_IGNORE;
    }
    const formData = new FormData();
    formData.append("file", file, file.name);
    try {
      const { data } = await axAPI.post(
        "/cms/image/upload/type?type=PUSH",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      form.setFieldValue("imageUrl", `${THUMBOR_URL}${data}`);
    } catch {
      notification.error({ message: "Не удалось загрузить картинку" });
    }
    return false;
  };

  const messageInputRef = useRef<TextAreaRef>(null);
  const titleInputRef = useRef<InputRef>(null);

  function updateCursorPosition() {
    const inputElement = messageInputRef?.current?.resizableTextArea?.textArea;
    if (inputElement?.selectionStart || inputElement?.selectionStart === 0) {
      setCursorPosition(Number(inputElement.selectionStart));
    }
  }
  function updateCursorPositionTitle() {
    const inputElement = titleInputRef?.current?.input;
    if (inputElement?.selectionStart || inputElement?.selectionStart === 0) {
      setCursorPositionTitle(Number(inputElement.selectionStart));
    }
  }

  function onEmojiClickTitle(emojiData: EmojiClickData) {
    const inputElement = titleInputRef?.current?.input;
    const index = inputElement?.selectionStart || cursorPositionTitle;

    let leftPart, rightPart;
    if (index == 0) {
      leftPart = "";
      rightPart = inputElement?.value;
    } else {
      leftPart = inputElement?.value.slice(0, index);
      rightPart = inputElement?.value.slice(index);
    }

    inputElement?.setSelectionRange(index + emojiData.emoji.length, null);
    setCursorPositionTitle(Number(index + emojiData.emoji.length));
    const newMessage = `${leftPart}${emojiData.emoji}${rightPart}`;
    console.log("newMessage", newMessage);
    form.setFieldValue("title", newMessage);
  }

  function onEmojiClick(emojiData: EmojiClickData) {
    const inputElement = messageInputRef?.current?.resizableTextArea?.textArea;
    const index = inputElement?.selectionStart || cursorPosition;

    let leftPart, rightPart;
    if (index == 0) {
      leftPart = "";
      rightPart = inputElement?.value;
    } else {
      leftPart = inputElement?.value.slice(0, index);
      rightPart = inputElement?.value.slice(index);
    }

    inputElement?.setSelectionRange(index + emojiData.emoji.length, null);
    setCursorPosition(Number(index + emojiData.emoji.length));
    const newMessage = `${leftPart}${emojiData.emoji}${rightPart}`;
    form.setFieldValue("message", newMessage);
  }

  async function loadTeams(leagueId: number, groupId?: number) {
    if (leagueId) {
      let seasonId;
      switch (leagueId) {
        case 100:
          seasonId = data?.data.activeSeasonInfo.activeSeasonMelbet[0].seasonId;
          break;
        case 200:
          seasonId = data?.data.activeSeasonInfo.activeSeasonLeonA[0].seasonId;
          break;
        case 300:
          seasonId = data?.data.activeSeasonInfo.activeSeasonLeonB[0].seasonId;
          break;
      }
      const { data: teams } = await axAPI.get("cms/info/teamsBySeason", {
        params: { leagueId, seasonId, groupId },
      });
      return teams;
    } else {
      return [];
    }
  }

  useEffect(() => {
    if (!useSegments) {
      loadTeams(leagueId, groupId).then((teams) => {
        setTeams(teams);
      });
    }
  }, [leagueId, groupId, useSegments]);

  // fnl-12134: поллинг статистики отправки. Рассылка асинхронная (Kafka →
  // notification-service), поэтому «сколько успешно отправлено» появляется
  // в push_logs только после того, как сервис доотправит все токены.
  const pollDispatchResult = (pushId: number) => {
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 2500;
    const POLL_TIMEOUT_MS = 3 * 60 * 1000;

    const poll = async () => {
      try {
        const { data } = await axAPI.get<PushDispatchStats>(
          "/cms/notification/result",
          { params: { pushId } }
        );
        if (data.finished) {
          if (data.tokensTotal !== null) {
            // 12251: две ветки отправки. FCM-счётчики приходят из
            // notification-service, AppMetrica — из cms-app; установки,
            // ушедшие через AppMetrica, из FCM-выборки исключены, поэтому
            // складывать их нельзя, показываем раздельно.
            const fcm = `FCM — ${data.sentSuccess ?? 0} из ${data.tokensTotal}${
              data.sentFailed ? ` (с ошибкой — ${data.sentFailed})` : ""
            }`;
            const appmetrica =
              data.sentAppmetrica !== null && data.sentAppmetrica !== undefined
                ? `, AppMetrica — ${data.sentAppmetrica}`
                : "";

            notification.success({
              message: "Рассылка завершена",
              description: `Отправлено: ${fcm}${appmetrica}`,
              duration: 0,
            });
          }
          return;
        }
      } catch (e) {
        // разовая сетевая ошибка — просто попробуем ещё раз
      }
      if (Date.now() - startedAt < POLL_TIMEOUT_MS) {
        setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        notification.info({
          message: "Рассылка ещё выполняется",
          description:
            "Статистика отправки появится в разделе «Статистика пушей»",
        });
      }
    };

    setTimeout(poll, POLL_INTERVAL_MS);
  };

  const onSubmitForm = async (values: any) => {
    console.log(values, "values");

    const { sendLink, testSend, debugToken, useSegments, segmentIds, ...rest } =
      values;
    let payload = { ...rest, testSend, debugToken };

    if (sendLink) {
      payload = { ...payload, link, sendLink };
    }

    // Проверяем, что выбран один из способов отправки
    // if (!useSegments && !rest.leagueId && !rest.teamId) {
    //   // Простая проверка без setFields
    //   console.error("Необходимо выбрать либо сегменты, либо лигу и команду");
    //   return;
    // }

    if (useSegments && segmentIds && segmentIds.length > 0) {
      payload = { ...payload, useSegments, segmentIds };
      // Убираем поля лиг и команд при отправке по сегментам
      delete payload.leagueId;
      delete payload.groupId;
      delete payload.teamId;
    } else if (!useSegments) {
      // Убираем поля сегментов при отправке по лигам
      delete payload.useSegments;
      delete payload.segmentIds;
    }

    const response = (await onFinish(payload)) as
      | { data?: { pushId?: number | null } }
      | undefined;

    // Счётчики есть только у сегментных рассылок: topic-рассылку (по лиге/клубу)
    // FCM разветвляет сам и число получателей не сообщает.
    const pushId = response?.data?.pushId;
    if (pushId && payload.useSegments && !testSend) {
      pollDispatchResult(pushId);
    }
  };

  return (
    <Show
      isLoading={formLoading || isLoading}
      title={
        <AppTitle isLoading={formLoading || isLoading}>
          {"Отправка push-уведомления"}
        </AppTitle>
      }
      breadcrumb={false}
      footerButtons={null}
      goBack={false}
      headerButtons={<></>}
    >
      <Form
        {...formProps}
        layout={"vertical"}
        onValuesChange={(changedValues, allValues) => {
          console.log("Изменено:", changedValues);
          console.log("Все значения:", allValues);
        }}
        onFinish={onSubmitForm}
        initialValues={{
          leagueId: null,
          teamId: null,
          link: null,
          imageUrl: null,
          sendLink: false,
          testSend: false,
          debugToken: null,
          useSegments: false,
          segmentIds: [],
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Flex align="center" gap={5} justify="space-between">
              <Form.Item
                name={"title"}
                label="Заголовок"
                style={{ width: "100%" }}
                rules={[
                  { required: true, message: "Необходимо указать заголовок" },
                  {
                    max: 120,
                    min: 3,
                    message: "Не более 120 символов и не менее 3 символов",
                  },
                ]}
              >
                <Input
                  ref={titleInputRef}
                  onInput={updateCursorPositionTitle}
                  onBlur={updateCursorPositionTitle}
                  placeholder={"Укажите название"}
                ></Input>
              </Form.Item>
              <Button
                onClick={() => {
                  setEmojiMessageOpen(false);
                  setEmojiTittleOpen(!emojiTittleOpen);
                }}
                type="text"
              >
                <SmileOutlined />
              </Button>
            </Flex>
            <Form.Item name="imageUrl" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Картинка">
              <Space direction="vertical">
                <Space>
                  <Upload
                    accept="image/png,image/jpeg"
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={uploadPushImage}
                  >
                    <Button>
                      {imageUrl ? "Заменить картинку" : "Загрузить картинку"}
                    </Button>
                  </Upload>
                  {imageUrl && (
                    <Button
                      onClick={() => form.setFieldValue("imageUrl", null)}
                    >
                      Убрать
                    </Button>
                  )}
                </Space>
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt=""
                    style={{
                      maxWidth: 320,
                      maxHeight: 180,
                      objectFit: "contain",
                    }}
                  />
                )}
                <Typography.Text type="secondary">
                  JPG или PNG до 1 МБ. Показывается под текстом на Android и в
                  раскрытом пуше на iOS.
                </Typography.Text>
              </Space>
            </Form.Item>
            <Flex align="center" gap={5} justify="space-between">
              <Form.Item
                name={"message"}
                label={"Сообщение:"}
                style={{ width: "100%" }}
                rules={[
                  { max: 1500, message: "Не более 1500 символов " },
                  { required: true, message: "Необходимо указать сообщение" },
                ]}
              >
                <Input.TextArea
                  ref={messageInputRef}
                  rows={20}
                  placeholder={"Укажите сообщение"}
                  onInput={updateCursorPosition}
                  onBlur={updateCursorPosition}
                ></Input.TextArea>
              </Form.Item>
              <Button
                onClick={() => {
                  setEmojiTittleOpen(false);
                  setEmojiMessageOpen(!emojiMessageOpen);
                }}
                type="text"
              >
                <SmileOutlined />
              </Button>
            </Flex>

            {/* Селектор сегментов */}
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px" }}>
                Отправка по сегментам
              </Text>
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                Выберите один из способов отправки: по сегментам или по лигам и
                командам
              </div>
            </div>
            <SegmentSelector form={form} />

            {/* Разделитель */}
            {!useSegments && (
              <div
                style={{ margin: "16px 0", borderTop: "1px solid #f0f0f0" }}
              />
            )}

            {/* Поля для лиг и команд - показываем только если не выбраны сегменты */}
            {!useSegments && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Отправка по лигам и командам
                  </Text>
                </div>
                <Form.Item name="leagueId" label={"Лига"}>
                  <Select placeholder="Выберите лигу">
                    <Select.Option key={null} value={null}>
                      ФНЛ (все лиги)
                    </Select.Option>
                    <Select.Option key={100} value={100}>
                      PARI - Первая лига
                    </Select.Option>
                    <Select.Option key={200} value={200}>
                      LEON - Вторая лига А
                    </Select.Option>
                    <Select.Option key={300} value={300}>
                      LEON - Вторая лига Б
                    </Select.Option>
                  </Select>
                </Form.Item>
                {(leagueId == 200 || leagueId == 300) && (
                  <Form.Item name="groupId" label={"Группа"}>
                    <Select placeholder="Выберите группу">
                      <Select.Option key={null} value={null}>
                        Все группы
                      </Select.Option>
                      {leagueId == 200 &&
                        data?.data.groupInfo.groupInfoLeonA.map(
                          (groupInfo: GroupInfoType) => (
                            <Select.Option
                              key={groupInfo.id}
                              value={groupInfo.id}
                            >
                              {groupInfo.name}
                            </Select.Option>
                          )
                        )}
                      {leagueId == 300 &&
                        data?.data.groupInfo.groupInfoLeonB.map(
                          (groupInfo: GroupInfoType) => (
                            <Select.Option
                              key={groupInfo.id}
                              value={groupInfo.id}
                            >
                              {groupInfo.name}
                            </Select.Option>
                          )
                        )}
                    </Select>
                  </Form.Item>
                )}
                {leagueId && (
                  <Form.Item name="teamId" label={"Клуб"}>
                    <Select placeholder="Выберите клуб">
                      <Select.Option key={null} value={null}>
                        Все клубы
                      </Select.Option>
                      {teams.map((team: TeamType) => (
                        <Select.Option key={team.teamId} value={team.teamId}>
                          {team.title}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </>
            )}

            <Form.Item name="sendLink" valuePropName="checked">
              <Checkbox>
                Отправить ссылку на новость или profile - для отправки ссылки на
                профиль
              </Checkbox>
            </Form.Item>
            <Form.Item name="testSend" valuePropName="checked">
              <Checkbox>Тестовая отправка </Checkbox>
            </Form.Item>
            {testSend && (
              <Form.Item name="debugToken" label="Получатель">
                <Select placeholder="Выберите получателя">
                  {testFcmUsers.map((user) => (
                    <Select.Option key={user.token} value={user.token}>
                      {user.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {sendLink && (
              <Form.Item name="link" label="Ссылка">
                <Input placeholder="Укажите ссылку на новость / профиль (указать текст profile)" />
              </Form.Item>
            )}
            <Space>
              <Button type={"primary"} htmlType={"submit"}>
                {"Отправить"}
              </Button>
            </Space>
          </Col>
          <Col span={12}>
            <EmojiPicker
              style={{ marginTop: "150px" }}
              open={emojiMessageOpen}
              onEmojiClick={onEmojiClick}
            />
            <EmojiPicker
              open={emojiTittleOpen}
              onEmojiClick={onEmojiClickTitle}
            />
          </Col>
        </Row>
      </Form>
    </Show>
  );
};
